import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import OpportunityDocumentsPanel from '../components/OpportunityDocumentsPanel';
import OpportunityFolderTree from '../components/OpportunityFolderTree';
import { Opportunity, OpportunityDocument, OpportunityFolderTreeNode, OpportunityLinkablePurchase } from '../types';
import { opportunitiesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { formatApiErrorDetail, translateKnownEnglishApiDetail } from '../utils/apiError';
import { userCanAccessPurchaseTabs } from '../constants/opportunityAccess';
import { getPurchaseDisplayId, resolvePurchaseTechnicalIdFromInput } from '../utils/purchaseDisplayId';
import './OpportunityDetail.css';

/** Pastas e documentos: UI dedicada (árvore + tabela), não montada via WorkflowCustomField / CustomFieldsEditor
 *  (esses ligam-se a requisições de compra com workflow_id). */
const normalizeText = (value: string): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const extractOpportunityBusinessNumber = (
  values: Array<{ text_value?: string; custom_field?: { name?: string; label?: string; description?: string } }>
): string | null => {
  for (const value of values) {
    const textValue = String(value.text_value || '').trim();
    if (!textValue) continue;

    const field = value.custom_field;
    const metadata = normalizeText(
      `${field?.name || ''} ${field?.label || ''} ${field?.description || ''}`
    );
    const hasOpportunityToken = metadata.includes('oportun');
    const hasNumberToken =
      metadata.includes('numero') ||
      metadata.includes('n°') ||
      metadata.includes('nº') ||
      metadata.includes('num');
    if (hasOpportunityToken && hasNumberToken) {
      return textValue.replace(/^#\s*/, '');
    }
  }
  return null;
};

const OpportunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const opportunityId = Number(id);
  const canEdit = userCanAccessPurchaseTabs(user);

  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [folders, setFolders] = useState<OpportunityFolderTreeNode[]>([]);
  const [documents, setDocuments] = useState<OpportunityDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsTotal, setDocumentsTotal] = useState(0);
  const [documentsSkip, setDocumentsSkip] = useState(0);
  const [documentsLimit, setDocumentsLimit] = useState(20);
  const [documentSearch, setDocumentSearch] = useState('');
  const [searchAllFolders, setSearchAllFolders] = useState(false);
  const [includeSubfolders, setIncludeSubfolders] = useState(false);
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderModalTitle, setFolderModalTitle] = useState('Nova pasta');
  const [folderName, setFolderName] = useState('');
  const [folderParentId, setFolderParentId] = useState<number | null>(null);
  const [renameFolderId, setRenameFolderId] = useState<number | null>(null);
  const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
  const [moveFolderId, setMoveFolderId] = useState<number | null>(null);
  const [moveFolderName, setMoveFolderName] = useState('');
  const [moveFolderParentId, setMoveFolderParentId] = useState<number | null>(null);
  const [showRenameDocumentModal, setShowRenameDocumentModal] = useState(false);
  const [renameDocumentId, setRenameDocumentId] = useState<number | null>(null);
  const [renameDocumentName, setRenameDocumentName] = useState('');
  const [showLinkPurchaseModal, setShowLinkPurchaseModal] = useState(false);
  const [linkPurchaseInput, setLinkPurchaseInput] = useState('');
  const [linkablePurchases, setLinkablePurchases] = useState<OpportunityLinkablePurchase[]>([]);
  const [loadingLinkablePurchases, setLoadingLinkablePurchases] = useState(false);
  const [savingPurchaseLink, setSavingPurchaseLink] = useState(false);
  const [linkModalError, setLinkModalError] = useState('');
  const [opportunityBusinessNumber, setOpportunityBusinessNumber] = useState<string | null>(null);

  const loadOpportunity = useCallback(async () => {
    const data = await opportunitiesApi.getById(opportunityId);
    setOpportunity(data);

    if (!data.purchase_request_id) {
      setOpportunityBusinessNumber(null);
      return;
    }
    try {
      const customValues = await opportunitiesApi.getPurchaseCustomFieldValues(data.purchase_request_id);
      setOpportunityBusinessNumber(extractOpportunityBusinessNumber(customValues));
    } catch {
      setOpportunityBusinessNumber(null);
    }
  }, [opportunityId]);

  const loadFolders = useCallback(async () => {
    const tree = await opportunitiesApi.getFolderTree(opportunityId);
    setFolders(tree);
  }, [opportunityId]);

  const loadLinkablePurchases = useCallback(
    async (searchTerm?: string) => {
      if (!canEdit) return;
      setLoadingLinkablePurchases(true);
      try {
        const items = await opportunitiesApi.listLinkablePurchases(opportunityId, searchTerm?.trim() || undefined);
        setLinkablePurchases(items);
      } catch (err: any) {
        setLinkModalError(
          formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao buscar requisições para vínculo.')
        );
      } finally {
        setLoadingLinkablePurchases(false);
      }
    },
    [canEdit, opportunityId]
  );

  const loadDocuments = useCallback(async () => {
    if (!canEdit) return;
    setDocumentsLoading(true);
    try {
      const response = await opportunitiesApi.queryDocuments(opportunityId, {
        search: documentSearch.trim() || undefined,
        folder_id: selectedFolderId,
        include_subfolders: includeSubfolders,
        scope_all: searchAllFolders,
        skip: documentsSkip,
        limit: documentsLimit,
      });
      setDocuments(response.items);
      setDocumentsTotal(response.total);
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao carregar a lista de documentos.'));
    } finally {
      setDocumentsLoading(false);
    }
  }, [
    opportunityId,
    selectedFolderId,
    documentSearch,
    includeSubfolders,
    searchAllFolders,
    documentsSkip,
    documentsLimit,
    canEdit,
  ]);

  const loadAll = useCallback(async () => {
    if (!canEdit) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Evita flicker global na troca de pasta: aqui carregamos apenas dados-base da oportunidade.
      await Promise.all([loadOpportunity(), loadFolders()]);
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao carregar oportunidade.'));
    } finally {
      setLoading(false);
    }
  }, [canEdit, loadFolders, loadOpportunity]);

  useEffect(() => {
    if (!Number.isFinite(opportunityId) || opportunityId <= 0) {
      setError('ID da oportunidade inválido.');
      return;
    }
    loadAll();
  }, [opportunityId, loadAll]);

  useEffect(() => {
    if (!loading) {
      void loadDocuments();
    }
  }, [selectedFolderId, loading, loadDocuments]);

  useEffect(() => {
    // Ao mudar de pasta: só arquivos diretamente nessa pasta (evita misturar com subpastas).
    setSearchAllFolders(false);
    setIncludeSubfolders(false);
  }, [selectedFolderId]);

  useEffect(() => {
    setDocumentsSkip(0);
  }, [selectedFolderId, documentSearch, searchAllFolders, includeSubfolders]);

  const openCreateFolder = (parentId?: number | null) => {
    setFolderModalTitle('Nova pasta');
    setFolderName('');
    setFolderParentId(parentId ?? null);
    setRenameFolderId(null);
    setShowFolderModal(true);
  };

  const openRenameFolder = (folder: OpportunityFolderTreeNode) => {
    setFolderModalTitle('Renomear pasta');
    setFolderName(folder.name);
    setFolderParentId(folder.parent_folder_id ?? null);
    setRenameFolderId(folder.id);
    setShowFolderModal(true);
  };

  const openMoveFolder = (folder: OpportunityFolderTreeNode) => {
    setMoveFolderId(folder.id);
    setMoveFolderName(folder.name);
    setMoveFolderParentId(folder.parent_folder_id ?? null);
    setShowMoveFolderModal(true);
  };

  const openRenameDocument = (document: OpportunityDocument) => {
    setRenameDocumentId(document.id);
    setRenameDocumentName(document.filename);
    setShowRenameDocumentModal(true);
  };

  const saveFolder = async () => {
    if (!canEdit) return;
    if (!folderName.trim()) {
      setError('Nome da pasta é obrigatório.');
      return;
    }
    setError('');
    try {
      if (renameFolderId) {
        await opportunitiesApi.renameFolder(opportunityId, renameFolderId, folderName.trim());
      } else {
        await opportunitiesApi.createFolder(opportunityId, {
          name: folderName.trim(),
          parent_folder_id: folderParentId,
        });
      }
      setShowFolderModal(false);
      await loadFolders();
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao salvar pasta.'));
    }
  };

  const uploadDocument = async (file: File) => {
    if (!canEdit) {
      setError('Você não possui permissão para enviar documentos.');
      return;
    }
    setError('');
    try {
      await opportunitiesApi.uploadDocument(opportunityId, file, selectedFolderId);
      await loadDocuments();
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao enviar o documento.'));
    }
  };

  const moveDocument = async (documentId: number, folderId?: number | null) => {
    if (!canEdit) {
      setError('Você não possui permissão para mover documentos.');
      return;
    }
    setError('');
    try {
      await opportunitiesApi.moveDocument(opportunityId, documentId, folderId ?? null);
      await loadDocuments();
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao mover documento.'));
    }
  };

  const moveDocumentsBatch = async (documentIds: number[], folderId?: number | null) => {
    if (!canEdit || documentIds.length === 0) return;
    try {
      await opportunitiesApi.moveDocumentsBatch(opportunityId, documentIds, folderId ?? null);
      await loadDocuments();
      showAlert({ title: 'Sucesso', message: 'Arquivos movidos com sucesso.', variant: 'success' });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao mover arquivos selecionados.'));
    }
  };

  const deleteDocumentsBatch = async (documentIds: number[]) => {
    if (!canEdit || documentIds.length === 0) return;
    const confirmed = await showConfirm({
      title: 'Excluir arquivos selecionados',
      message: `Deseja excluir ${documentIds.length} arquivo(s) selecionado(s)?`,
      details: 'Esta ação não poderá ser desfeita.',
      variant: 'danger',
      confirmLabel: 'Excluir selecionados',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      const response = await opportunitiesApi.deleteDocumentsBatch(opportunityId, documentIds);
      await loadDocuments();
      showAlert({
        title: 'Sucesso',
        message: `${response.deleted_documents} arquivo(s) excluído(s) com sucesso.`,
        variant: 'success',
      });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao excluir arquivos selecionados.'));
    }
  };

  const saveMoveFolder = async () => {
    if (!canEdit || moveFolderId == null) return;
    try {
      await opportunitiesApi.moveFolder(opportunityId, moveFolderId, moveFolderParentId);
      setShowMoveFolderModal(false);
      await Promise.all([loadFolders(), loadDocuments()]);
      showAlert({ title: 'Sucesso', message: 'Pasta movida com sucesso.', variant: 'success' });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao mover pasta.'));
    }
  };

  const saveRenameDocument = async () => {
    if (!canEdit || renameDocumentId == null) return;
    const normalized = renameDocumentName.trim();
    if (!normalized) {
      setError('Nome do arquivo é obrigatório.');
      return;
    }
    try {
      await opportunitiesApi.renameDocument(opportunityId, renameDocumentId, normalized);
      setShowRenameDocumentModal(false);
      await loadDocuments();
      showAlert({ title: 'Sucesso', message: 'Arquivo renomeado com sucesso.', variant: 'success' });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao renomear arquivo.'));
    }
  };

  const deleteDocument = async (document: OpportunityDocument) => {
    if (!canEdit) {
      setError('Você não possui permissão para excluir documentos.');
      return;
    }

    const confirmed = await showConfirm({
      title: 'Excluir arquivo',
      message: `Deseja realmente excluir o arquivo "${document.filename}"?`,
      details: 'Esta ação não poderá ser desfeita.',
      variant: 'danger',
      confirmLabel: 'Excluir arquivo',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;

    try {
      await opportunitiesApi.deleteDocument(opportunityId, document.id);
      await loadDocuments();
      showAlert({ title: 'Sucesso', message: 'Arquivo excluído com sucesso.', variant: 'success' });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao excluir arquivo.'));
    }
  };

  const downloadOpportunityDocument = useCallback(
    async (doc: OpportunityDocument) => {
      if (!canEdit) {
        setError('Você não possui permissão para baixar documentos.');
        return;
      }
      try {
        await opportunitiesApi.downloadDocument(opportunityId, doc.id, doc.filename);
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? translateKnownEnglishApiDetail(err.message)
            : formatApiErrorDetail((err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail, 'Não foi possível baixar o arquivo.');
        showAlert({ title: 'Baixar arquivo', message, variant: 'danger' });
      }
    },
    [canEdit, opportunityId, showAlert]
  );

  const deleteFolder = async (folder: OpportunityFolderTreeNode) => {
    if (!canEdit) {
      setError('Você não possui permissão para excluir pastas.');
      return;
    }

    let preview;
    try {
      preview = await opportunitiesApi.getFolderDeletePreview(opportunityId, folder.id);
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao carregar pré-visualização da exclusão.'));
      return;
    }

    const folderLines = preview.folders.map((name) => `- Pasta: ${name}`);
    const documentLines = preview.documents.map((name) => `- Arquivo: ${name}`);
    const extraFolders = preview.has_more_folders ? '\n- ... outras pastas' : '';
    const extraDocuments = preview.has_more_documents ? '\n- ... outros arquivos' : '';
    const details = [
      `Serão excluídos ${preview.total_folders} pasta(s) e ${preview.total_documents} arquivo(s).`,
      '',
      ...folderLines,
      ...documentLines,
      `${extraFolders}${extraDocuments}`.trim(),
    ]
      .filter((line) => line !== '')
      .join('\n');

    const step1 = await showConfirm({
      title: 'Excluir pasta e conteúdo',
      message: `Deseja prosseguir com a exclusão de «${preview.folder_name}» e de todo o conteúdo abaixo desta pasta?`,
      details,
      variant: 'warning',
      confirmLabel: 'Continuar',
      cancelLabel: 'Cancelar',
    });
    if (!step1) return;

    const step2 = await showConfirm({
      title: 'Confirmação final',
      message: `«${preview.folder_name}» e ${preview.total_folders} pasta(s), ${preview.total_documents} arquivo(s) serão removidos permanentemente.`,
      details:
        'Em produção isto pode afetar auditoria e documentos com valor legal. Só prossiga se tiver certeza absoluta.',
      variant: 'danger',
      confirmLabel: 'Excluir definitivamente',
      cancelLabel: 'Voltar',
    });
    if (!step2) return;

    try {
      await opportunitiesApi.deleteFolder(opportunityId, folder.id);
      if (selectedFolderId === folder.id) {
        setSelectedFolderId(null);
      }
      await Promise.all([loadFolders(), loadDocuments()]);
      showAlert({ title: 'Sucesso', message: 'Pasta e conteúdo excluídos com sucesso.', variant: 'success' });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao excluir pasta.'));
    }
  };

  const flatFolders = useMemo(() => {
    const list: Array<{ id: number; name: string; parent_folder_id?: number | null }> = [];
    const walk = (nodes: OpportunityFolderTreeNode[]) => {
      for (const node of nodes) {
        list.push({ id: node.id, name: node.name, parent_folder_id: node.parent_folder_id });
        walk(node.children);
      }
    };
    walk(folders);
    return list;
  }, [folders]);

  const currentPage = Math.floor(documentsSkip / documentsLimit) + 1;
  const totalPages = Math.max(1, Math.ceil(documentsTotal / documentsLimit));

  const invalidMoveTargets = useMemo(() => {
    if (moveFolderId == null) return new Set<number>();
    const set = new Set<number>();
    const visit = (nodes: OpportunityFolderTreeNode[]): boolean => {
      for (const node of nodes) {
        if (node.id === moveFolderId) {
          set.add(node.id);
          const mark = (children: OpportunityFolderTreeNode[]) => {
            for (const child of children) {
              set.add(child.id);
              mark(child.children);
            }
          };
          mark(node.children);
          return true;
        }
        if (visit(node.children)) return true;
      }
      return false;
    };
    visit(folders);
    return set;
  }, [folders, moveFolderId]);

  const getFolderLocationLabel = useCallback(
    (parentFolderId: number | null): string => {
      const opportunityNumber = opportunityBusinessNumber || String(opportunityId);
      if (parentFolderId == null) return `Raiz da oportunidade #${opportunityNumber}`;
      const visit = (nodes: OpportunityFolderTreeNode[], trail: string[]): string | null => {
        for (const node of nodes) {
          const nextTrail = [...trail, node.name];
          if (node.id === parentFolderId) {
            return nextTrail.join(' / ');
          }
          const found = visit(node.children, nextTrail);
          if (found) return found;
        }
        return null;
      };
      return visit(folders, []) ?? `Pasta #${parentFolderId}`;
    },
    [folders, opportunityBusinessNumber, opportunityId]
  );

  const breadcrumbs = useMemo(() => {
    const opportunityNumber = opportunityBusinessNumber || String(opportunityId);
    if (selectedFolderId == null) return `Raiz da oportunidade #${opportunityNumber}`;
    const stack: string[] = [];

    const visit = (nodes: OpportunityFolderTreeNode[], targetId: number, trail: string[]): boolean => {
      for (const node of nodes) {
        const nextTrail = [...trail, node.name];
        if (node.id === targetId) {
          stack.push(...nextTrail);
          return true;
        }
        if (visit(node.children, targetId, nextTrail)) {
          return true;
        }
      }
      return false;
    };

    visit(folders, selectedFolderId, []);
    return stack.join(' / ') || `Raiz da oportunidade #${opportunityNumber}`;
  }, [folders, selectedFolderId, opportunityBusinessNumber, opportunityId]);

  const linkedPurchaseDisplayId = useMemo(() => {
    if (!opportunity?.purchase_request_id) return null;
    return opportunity.purchase_request?.public_id || String(opportunity.purchase_request_id);
  }, [opportunity]);

  const openPurchaseLinkModal = async () => {
    setLinkModalError('');
    setLinkPurchaseInput(linkedPurchaseDisplayId ? `#${linkedPurchaseDisplayId}` : '');
    setShowLinkPurchaseModal(true);
    await loadLinkablePurchases('');
  };

  const savePurchaseLink = async () => {
    if (!canEdit || !opportunity) return;
    setSavingPurchaseLink(true);
    setLinkModalError('');
    try {
      const normalizedLinkInput = linkPurchaseInput.trim();
      let payload: number | null = null;
      let payloadPublicId: string | null = null;
      let payloadDisplayId: string | null = null;
      if (normalizedLinkInput) {
        const idMatch = normalizedLinkInput.match(/^#?\s*(\d+(?:\.\d+)?)\b/);
        if (!idMatch) {
          setLinkModalError('Digite e selecione uma requisição pelo número (ex.: #10001 ou #10001.1).');
          setSavingPurchaseLink(false);
          return;
        }
        payloadPublicId = idMatch[1];
        const resolvedId = resolvePurchaseTechnicalIdFromInput(normalizedLinkInput, linkablePurchases);
        if (resolvedId !== null) {
          payload = resolvedId;
          const selectedPurchase = linkablePurchases.find((purchase) => purchase.id === resolvedId);
          payloadDisplayId = selectedPurchase ? getPurchaseDisplayId(selectedPurchase) : payloadPublicId;
        } else {
          payloadDisplayId = payloadPublicId;
        }
      }
      const updated = await opportunitiesApi.updatePurchaseLink(opportunity.id, {
        purchase_request_id: payload,
        purchase_public_id: payloadPublicId,
      });
      setOpportunity(updated);
      setShowLinkPurchaseModal(false);
      showAlert({
        title: 'Vínculo atualizado',
        message:
          payload == null
            ? 'A oportunidade foi desvinculada da requisição.'
            : `A oportunidade foi vinculada à requisição #${payloadDisplayId || payload}.`,
        variant: 'success',
      });
    } catch (err: any) {
      setLinkModalError(
        formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao atualizar vínculo da requisição.')
      );
    } finally {
      setSavingPurchaseLink(false);
    }
  };

  const applyModuleDefaultsUnderSelection = async (parentFolderId: number | null) => {
    if (!canEdit) return;
    const whereLabel = getFolderLocationLabel(parentFolderId);
    const confirmed = await showConfirm({
      title: 'Aplicar conjunto de pastas do módulo',
      message: `Criar aqui as pastas definidas na configuração global do módulo Oportunidades (lista «Pastas na raiz»), como subpastas de «${whereLabel}»?`,
      details:
        'Se já existir uma pasta com o mesmo nome neste nível, essa entrada é ignorada. A numeração (1., 2., …) segue a opção da configuração do módulo.',
      variant: 'primary',
      confirmLabel: 'Criar pastas',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    setError('');
    try {
      const res = await opportunitiesApi.applyModuleDefaultFolders(opportunityId, parentFolderId);
      await loadFolders();
      showAlert({
        title: 'Conjunto aplicado',
        message: `${res.created_count} pasta(s) criada(s).${
          res.skipped_count ? ` ${res.skipped_count} ignorada(s) (nome já existia neste nível).` : ''
        }`,
        variant: 'success',
      });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao aplicar o conjunto de pastas.'));
    }
  };

  if (loading) {
    return <div className="p-3">Carregando...</div>;
  }

  if (!opportunity) {
    if (!canEdit) {
      return (
        <div className="p-3">
          <Alert variant="warning">
            Você não possui permissão para acessar oportunidades de contratação.
          </Alert>
        </div>
      );
    }
    return <div className="p-3">Oportunidade não encontrada.</div>;
  }

  return (
    <div className="p-3 opportunity-detail-page">
      <Card className="mb-3 opportunity-header-card">
        <Card.Body className="d-flex flex-column flex-md-row justify-content-between gap-3">
          <div>
            <h4 className="mb-1">{opportunity.title}</h4>
            <div className="text-muted">{opportunity.description || 'Sem descrição.'}</div>
            <div className="mt-1">
              {opportunity.purchase_request_id ? (
                <small className="text-muted">
                  Vinculada à requisição{' '}
                  <Badge bg="info" text="dark">
                    #{linkedPurchaseDisplayId}
                  </Badge>
                  {opportunity.purchase_request?.title ? ` - ${opportunity.purchase_request.title}` : ''}
                </small>
              ) : (
                <small className="text-muted">Sem vínculo com requisição de compras.</small>
              )}
            </div>
            <small className="text-secondary opportunity-current-path" title={breadcrumbs}>
              Pasta atual: {breadcrumbs}
            </small>
          </div>
          <div className="d-flex gap-2 align-items-start flex-wrap justify-content-end">
            <Button variant="outline-primary" onClick={() => void openPurchaseLinkModal()}>
              {opportunity.purchase_request_id ? 'Alterar vínculo' : 'Vincular requisição'}
            </Button>
            <Button variant="outline-secondary" onClick={() => navigate('/purchases/opportunities')}>
              Voltar
            </Button>
          </div>
        </Card.Body>
      </Card>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-3">
        <Col xs={12} lg={4}>
          <Card className="opportunity-left-card">
            <Card.Body>
              <Form.Control
                size="sm"
                placeholder="Pesquisar pasta"
                className="mb-2"
                value={folderSearchTerm}
                onChange={(event) => setFolderSearchTerm(event.target.value)}
              />
              <OpportunityFolderTree
                folders={folders}
                selectedFolderId={selectedFolderId}
                rootLabel={
                  opportunity.purchase_request_id
                    ? `Raiz da oportunidade #${opportunityBusinessNumber || opportunity.id} (Req #${linkedPurchaseDisplayId})`
                    : `Raiz da oportunidade #${opportunityBusinessNumber || opportunity.id}`
                }
                canEdit={canEdit}
                searchTerm={folderSearchTerm}
                onSelectFolder={setSelectedFolderId}
                onCreateFolder={openCreateFolder}
                onApplyModuleDefaults={(parentId) => void applyModuleDefaultsUnderSelection(parentId)}
                onRenameFolder={openRenameFolder}
                onMoveFolder={openMoveFolder}
                onDeleteFolder={deleteFolder}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={8}>
          <Card className="opportunity-right-card">
            <Card.Body>
              <Row className="g-2 mb-3 opportunity-filters-row">
                <Col xs={12} md={5}>
                  <Form.Control
                    size="sm"
                    placeholder="Pesquisar arquivo por nome/caminho"
                    value={documentSearch}
                    onChange={(event) => setDocumentSearch(event.target.value)}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <Form.Select
                    size="sm"
                    value={documentsLimit}
                    onChange={(event) => {
                      setDocumentsLimit(Number(event.target.value));
                      setDocumentsSkip(0);
                    }}
                  >
                    <option value={10}>10 por página</option>
                    <option value={20}>20 por página</option>
                    <option value={50}>50 por página</option>
                  </Form.Select>
                </Col>
                <Col xs={12} md={4} className="d-flex gap-2 align-items-center justify-content-md-end">
                  <div className="opportunity-switches d-flex flex-column flex-md-row gap-2">
                    <Form.Check
                      type="switch"
                      id="search-all-folders"
                      label="Buscar em toda oportunidade"
                      checked={searchAllFolders}
                      onChange={(event) => setSearchAllFolders(event.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="include-subfolders"
                      label="Incluir subpastas"
                      checked={includeSubfolders}
                      disabled={searchAllFolders}
                      onChange={(event) => setIncludeSubfolders(event.target.checked)}
                      title={
                        searchAllFolders
                          ? 'Indisponível enquanto «Buscar em toda oportunidade» está ligado.'
                          : 'Desligado (padrão): só arquivos nesta pasta. Ligado: inclui também subpastas abaixo desta.'
                      }
                    />
                  </div>
                </Col>
              </Row>
              {selectedFolderId != null && !searchAllFolders ? (
                <Row className="g-2 mb-2">
                  <Col xs={12}>
                    <Form.Text className="text-muted small">
                      Por padrão mostram-se só os arquivos <strong>desta</strong> pasta. Ative «Incluir
                      subpastas» apenas se quiser ver também o que está nas pastas filhas (a coluna Caminho
                      indica onde está cada arquivo).
                    </Form.Text>
                  </Col>
                </Row>
              ) : null}
              <OpportunityDocumentsPanel
                folders={folders}
                documents={documents}
                selectedFolderId={selectedFolderId}
                canEdit={canEdit}
                canDownload={canEdit}
                onUploadFile={uploadDocument}
                onMoveDocument={moveDocument}
                onBulkMoveDocuments={moveDocumentsBatch}
                onBulkDeleteDocuments={deleteDocumentsBatch}
                onRenameDocument={openRenameDocument}
                onDeleteDocument={deleteDocument}
                onDownloadDocument={downloadOpportunityDocument}
              />
              {documentsLoading && (
                <small className="text-muted d-block mt-2">Atualizando documentos...</small>
              )}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mt-3 gap-2">
                <small className="text-muted">
                  Total de arquivos encontrados: {documentsTotal}
                </small>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={documentsSkip <= 0}
                    onClick={() => setDocumentsSkip((prev) => Math.max(0, prev - documentsLimit))}
                  >
                    Anterior
                  </Button>
                  <small>
                    Página {currentPage} de {totalPages}
                  </small>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={documentsSkip + documentsLimit >= documentsTotal}
                    onClick={() => setDocumentsSkip((prev) => prev + documentsLimit)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showFolderModal} onHide={() => setShowFolderModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{folderModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Nome da pasta</Form.Label>
            <Form.Control
              value={folderName}
              maxLength={255}
              onChange={(event) => setFolderName(event.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowFolderModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveFolder} disabled={!canEdit}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMoveFolderModal} onHide={() => setShowMoveFolderModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Mover pasta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Pasta: <strong>{moveFolderName}</strong></p>
          <Form.Group>
            <Form.Label>Destino (pasta pai)</Form.Label>
            <Form.Select
              value={moveFolderParentId == null ? 'none' : String(moveFolderParentId)}
              onChange={(event) => {
                const value = event.target.value;
                setMoveFolderParentId(value === 'none' ? null : Number(value));
              }}
            >
              <option value="none">Raiz da oportunidade</option>
              {flatFolders
                .filter((folder) => !invalidMoveTargets.has(folder.id))
                .map((folder) => (
                  <option key={`move-folder-${folder.id}`} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowMoveFolderModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveMoveFolder} disabled={!canEdit}>
            Mover
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRenameDocumentModal} onHide={() => setShowRenameDocumentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Renomear arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Novo nome do arquivo</Form.Label>
            <Form.Control
              value={renameDocumentName}
              maxLength={500}
              onChange={(event) => setRenameDocumentName(event.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRenameDocumentModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveRenameDocument} disabled={!canEdit}>
            Renomear
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showLinkPurchaseModal}
        onHide={() => {
          setShowLinkPurchaseModal(false);
          setLinkModalError('');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Vincular oportunidade à requisição</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {linkModalError && <Alert variant="danger" className="py-2">{linkModalError}</Alert>}
          <Form.Group className="mb-2">
            <Form.Label>Requisição vinculada</Form.Label>
            <Form.Control
              list="linkable-purchases-list"
              value={linkPurchaseInput}
              placeholder="Digite número ou título (ex.: #10001 ou #10001.1)"
              onChange={(event) => {
                const next = event.target.value;
                setLinkPurchaseInput(next);
                void loadLinkablePurchases(next);
              }}
            />
            <datalist id="linkable-purchases-list">
              {linkablePurchases.map((purchase) => (
                <option
                  key={`link-pr-${purchase.id}`}
                  value={`#${getPurchaseDisplayId(purchase)} - ${purchase.title}`}
                />
              ))}
            </datalist>
            <Form.Text className="text-muted">
              Campo único com busca. Deixe vazio para remover o vínculo atual.
              {loadingLinkablePurchases ? ' Buscando...' : ''}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowLinkPurchaseModal(false);
              setLinkModalError('');
            }}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void savePurchaseLink()} disabled={savingPurchaseLink}>
            {savingPurchaseLink ? 'Salvando...' : 'Salvar vínculo'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OpportunityDetail;
